/**
 * noche-testimonios.js
 *
 * Custom Element for the Noche Testimonios slider.
 * Manual slider (no external library).
 * Responsive: 3 cols desktop, 2 tablet, 1 mobile.
 */

class NocheTestimoniosSlider extends HTMLElement {
  connectedCallback() {
    this._current   = 0;
    this._track     = this.querySelector('[data-track]');
    this._dotsEl    = this.querySelector('[data-dots]');
    this._prevBtn   = this.querySelector('[data-prev]');
    this._nextBtn   = this.querySelector('[data-next]');
    this._cards     = Array.from(this._track?.children ?? []);
    this._total     = this._cards.length;

    if (!this._track || this._total === 0) return;

    this._mq3 = window.matchMedia('(min-width: 1024px)');
    this._mq2 = window.matchMedia('(min-width: 750px) and (max-width: 1023px)');

    this._buildDots();
    this._update();

    this._prevBtn?.addEventListener('click', () => this._go(this._current - 1));
    this._nextBtn?.addEventListener('click', () => this._go(this._current + 1));

    const onBreakpoint = () => {
      this._current = 0;
      this._buildDots();
      this._update();
    };
    this._mq3.addEventListener('change', onBreakpoint);
    this._mq2.addEventListener('change', onBreakpoint);
  }

  /** Cuántas cards son visibles según el breakpoint actual */
  _visibleCount() {
    if (this._mq3.matches) return 3;
    if (this._mq2.matches) return 2;
    return 1;
  }

  /** Total de "páginas" posibles */
  _pageCount() {
    return Math.max(1, this._total - this._visibleCount() + 1);
  }

  _buildDots() {
    if (!this._dotsEl) return;
    this._dotsEl.innerHTML = '';

    const pages = this._pageCount();
    for (let i = 0; i < pages; i++) {
      const btn = document.createElement('button');
      btn.className = 'noche-testimonios__dot';
      btn.setAttribute('aria-label', `Ir al testimonio ${i + 1}`);
      btn.addEventListener('click', () => this._go(i));
      this._dotsEl.appendChild(btn);
    }
  }

  _go(index) {
    const pages = this._pageCount();
    this._current = Math.max(0, Math.min(index, pages - 1));
    this._update();
  }

  _update() {
    if (!this._cards.length) return;

    const cardWidth = this._cards[0].getBoundingClientRect().width;
    const gap       = 24;
    const offset    = this._current * (cardWidth + gap);

    this._track.style.transform = `translateX(-${offset}px)`;

    // Dots
    const dots = this._dotsEl?.querySelectorAll('.noche-testimonios__dot') ?? [];
    dots.forEach((dot, i) => {
      dot.classList.toggle('noche-testimonios__dot--active', i === this._current);
    });

    // Buttons
    const pages = this._pageCount();
    if (this._prevBtn) this._prevBtn.disabled = this._current === 0;
    if (this._nextBtn) this._nextBtn.disabled = this._current >= pages - 1;
  }
}

customElements.define('noche-testimonios-slider', NocheTestimoniosSlider);

/* ===== Video play / pause per card ===== */
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.noche-testimonios__video-wrapper').forEach((wrapper) => {
    const video    = wrapper.querySelector('.noche-testimonios__video');
    const playBtn  = wrapper.querySelector('[data-action="play"]');
    const pauseBtn = wrapper.querySelector('[data-action="pause"]');

    if (!video || !playBtn || !pauseBtn) return;

    function setPlaying(isPlaying) {
      wrapper.classList.toggle('is-playing', isPlaying);
      pauseBtn.style.display = isPlaying ? 'flex' : 'none';
    }

    playBtn.addEventListener('click', () => {
      const promise = video.play();
      if (promise !== undefined) {
        promise.then(() => setPlaying(true)).catch(() => {});
      }
    });

    pauseBtn.addEventListener('click', () => {
      video.pause();
      setPlaying(false);
    });

    video.addEventListener('play',  () => setPlaying(true));
    video.addEventListener('pause', () => setPlaying(false));
    video.addEventListener('ended', () => setPlaying(false));
  });
});
